import { get, has } from 'lodash'

// USHOP-2821 — pure helpers for the FE-driven Send Now 3DS flow (JP/feelgreat).
// See .tickets/USHOP-2821/SEND-NOW-3DS-FLOW.md for the backend contract.

/**
 * Device Data Collection runner.
 * The DDC endpoint returns `ddcHTML` (a Cardinal device-data-collection document) but NOT a
 * sessionId. The sessionId only exists after that HTML runs in the browser: it is mounted in a
 * hidden iframe and posts back `{ MessageType: 'profile.completed', SessionId, Status }`.
 *
 * Resolves the SessionId string ('' on timeout / empty html). Never rejects.
 */
export const runDdcHtml = (html, { timeoutMs = 15000 } = {}) =>
    new Promise(resolve => {
        if (!html) {
            resolve('')
            return
        }

        let settled = false
        const iframe = document.createElement('iframe')
        iframe.style.display = 'none'
        iframe.setAttribute('title', 'fg-ddc')
        iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin')
        iframe.srcdoc = html.replace(/\r\n/g, '')

        const cleanup = () => {
            window.removeEventListener('message', onMessage)
            clearTimeout(timer)
            if (iframe.parentNode) {
                iframe.parentNode.removeChild(iframe)
            }
        }

        const finish = sessionId => {
            if (settled) return
            settled = true
            cleanup()
            resolve(sessionId || '')
        }

        const onMessage = event => {
            const { data } = event || {}
            if (!data || data === 'PROXY_READY') return

            let msg
            try {
                msg = typeof data === 'string' ? JSON.parse(data) : data
            } catch (error) {
                return
            }

            if (msg && msg.MessageType === 'profile.completed') {
                finish(msg.SessionId)
            }
        }

        const timer = setTimeout(() => finish(''), timeoutMs)
        window.addEventListener('message', onMessage)
        document.body.appendChild(iframe)
    })

/**
 * Some environments may return the sessionId directly on the DDC response, in which case the
 * iframe step can be skipped. Tolerant of `data.*` and top-level shapes.
 */
export const readDirectSessionId = res =>
    get(res, 'data.sessionId') ||
    get(res, 'data.session_id') ||
    get(res, 'data.referenceId') ||
    get(res, 'data.threeDSServerTransID') ||
    get(res, 'sessionId') ||
    get(res, 'session_id') ||
    get(res, 'referenceId') ||
    get(res, 'threeDSServerTransID') ||
    ''

/**
 * Reads the prepare response. Tolerant of `data.*` / top-level and snake/camel casing.
 */
export const readPrepare = res => ({
    hydraBody: get(res, 'data.hydra_body') || get(res, 'data.hydraBody') || get(res, 'hydra_body') || get(res, 'hydraBody') || null,
    hydraCreateUrl:
        get(res, 'data.hydra_create_url') || get(res, 'data.hydraCreateUrl') || get(res, 'hydra_create_url') || get(res, 'hydraCreateUrl') || null
})

/**
 * Walks `hydra_body.transactions.items[]` and fills the secrets the prepare step left as
 * placeholders. Only overwrites keys that already exist on a `methodDetails` (card methods);
 * non-card methods are left untouched. Mutates and returns `hydraBody`.
 */
export const injectCardSecrets = (hydraBody, sessionId, cvv) => {
    const items = get(hydraBody, 'transactions.items', [])

    items.forEach(item => {
        const md = item && item.methodDetails
        if (!md) return

        if (has(md, 'creditCardSecurityCode')) {
            md.creditCardSecurityCode = cvv
        }
        if (has(md, 'sessionId')) {
            md.sessionId = `${sessionId}`
        }
    })

    return hydraBody
}

/**
 * Reads the created order id from the create response. The response body is wrapped
 * (`{ status, data: { order: { id: { unicity } } } }`), but axios puts that body under `.data`,
 * so from the axios response the order id lives at `data.data.order.id.unicity`. Tolerant of the
 * wrapped, unwrapped (`{ order }`) and raw-order shapes.
 */
export const readCreateOrderId = res => {
    const body = get(res, 'data', res)
    return get(body, 'data.order.id.unicity') || get(body, 'order.id.unicity') || get(body, 'id.unicity') || ''
}

/**
 * Whether the create call created the order. Note: in a 3DS market the success response still
 * carries `challengeHTML` in the transaction — that is NOT a failure, the order is created (the
 * top-level `status` is `success` and an order id is returned). Success is therefore driven by the
 * response `status` / order id, not by the absence of challenge HTML.
 */
export const isCreateSuccess = res => {
    const body = get(res, 'data', res)
    if (get(body, 'error') || get(body, 'data.error')) {
        return false
    }
    return get(body, 'status') === 'success' || Boolean(readCreateOrderId(res))
}

/**
 * Reads the 3DS challenge (step-up) HTML from the create response, if any transaction item carries it.
 * Wrapper/unwrapped/raw tolerant (see readCreateOrderId). Returns '' when there is no challenge.
 */
export const readChallengeHtml = res => {
    const body = get(res, 'data', res)
    const items = get(body, 'data.order.transactions.items') || get(body, 'order.transactions.items') || get(body, 'transactions.items') || []
    const item = items.find(it => get(it, 'methodDetails.challengeHTML'))
    return item ? get(item, 'methodDetails.challengeHTML') : ''
}

/**
 * Builds the `create_order` result object expected by the complete call.
 */
export const buildCreateOrderResult = ({ ok, response, hydraCreateUrl, hydraBody, headers }) => ({
    status: ok ? 'success' : 'fail',
    order_id: ok ? readCreateOrderId(response) : '',
    hydra_request: {
        method: 'post',
        url: hydraCreateUrl,
        headers,
        data: hydraBody
    },
    hydra_response_header: get(response, 'headers', {}),
    hydra_response_body: get(response, 'data', {})
})
